import { CheckCircleOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Empty, List, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { DomainLogo } from 'components/atoms/DomainLogo';
import { VendorForm } from 'components/atoms/VendorForm';
import { SalesforceSupplierSearchResult, VendorLookupSearchResult } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';

type VendorLookupPaneProps = {
  vendorLookupSearchLoading: boolean;
  vendorLookupResults: VendorLookupSearchResult[];
  selectedVendor?: number;
  selectedRecordsMatchBySalesforceId: boolean;
  selectedSalesforceSupplier?: SalesforceSupplierSearchResult;
  handleVendorChange: (vendorId?: number) => void;
  getSalesforceTagColor: (salesforceId?: string | null) => string;
  vendor?: VendorAdmin;
  updating: boolean;
  form: any;
  addOrEditVendor: boolean;
  handleSubmit: (values: any) => Promise<void>;
  handleCancel: () => void;
  handleEdit: () => void;
  canDeleteVendor: boolean;
  isActionLoading: boolean;
  openDeleteModal: () => void;
  paneHeaderLogoClass: string;
  paneHeaderFallbackClass: string;
};

export const VendorLookupPane: React.FC<VendorLookupPaneProps> = ({
  vendorLookupSearchLoading,
  vendorLookupResults,
  selectedVendor,
  selectedRecordsMatchBySalesforceId,
  selectedSalesforceSupplier,
  handleVendorChange,
  getSalesforceTagColor,
  vendor,
  updating,
  form,
  addOrEditVendor,
  handleSubmit,
  handleCancel,
  handleEdit,
  canDeleteVendor,
  isActionLoading,
  openDeleteModal,
  paneHeaderLogoClass,
  paneHeaderFallbackClass
}) => (
  <Col span={12}>
    <Card
      title={
        <div className="flex items-center gap-2">
          <DomainLogo
            name="NPI Financial"
            domainOrWebsite="npifinancial.com"
            className={paneHeaderLogoClass}
            fallbackClassName={paneHeaderFallbackClass}
          />
          <span>Vantage Vendors</span>
        </div>
      }
      className="h-full"
    >
      <div className="max-h-60 overflow-y-auto rounded border border-gray-200">
        {vendorLookupSearchLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : vendorLookupResults.length > 0 ? (
          <List
            dataSource={vendorLookupResults}
            renderItem={(vendorLookupItem) => {
              const isSelected = selectedVendor === vendorLookupItem.id;
              const isMatchedVendorRow = Boolean(
                selectedRecordsMatchBySalesforceId &&
                isSelected &&
                vendorLookupItem.salesforce_id &&
                vendorLookupItem.salesforce_id === selectedSalesforceSupplier?.Id
              );
              const vendorDealCount = vendorLookupItem.deal_count;

              return (
                <List.Item
                  className={`cursor-pointer px-4 py-2 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} border-b border-gray-100`}
                  onClick={() => handleVendorChange(vendorLookupItem.id)}
                >
                  <div className="flex w-full items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <DomainLogo
                        name={vendorLookupItem.name}
                        domainOrWebsite={vendorLookupItem.domain}
                        className="h-9 w-9 rounded object-contain"
                        fallbackClassName="flex h-9 w-9 items-center justify-center rounded-md bg-blue-100 text-sm font-bold text-blue-600"
                      />
                      <div className="min-w-0">
                        <Typography.Text strong>{vendorLookupItem.name}</Typography.Text>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <Tag
                            color={getSalesforceTagColor(vendorLookupItem.salesforce_id)}
                            data-sfdc-color-key={getSalesforceTagColor(vendorLookupItem.salesforce_id)}
                            className="!mr-0 rounded-full text-[11px]"
                          >
                            SFDC ID: {vendorLookupItem.salesforce_id || 'N/A'}
                          </Tag>
                          {isMatchedVendorRow && (
                            <Tag color="success" className="!mr-0 rounded-full text-[11px]">
                              <CheckCircleOutlined className="mr-1" />
                              Matched
                            </Tag>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-56 shrink-0 text-xs text-gray-500">
                      <Typography.Text className="block text-xs text-gray-500">
                        DB ID: {vendorLookupItem.id}
                      </Typography.Text>
                      <Typography.Text
                        className="block text-xs text-gray-500"
                        ellipsis={{ tooltip: `Domain: ${vendorLookupItem.domain || 'N/A'}` }}
                      >
                        Domain: {vendorLookupItem.domain || 'N/A'}
                      </Typography.Text>
                      <Typography.Text className="block text-xs text-gray-500">
                        Deal Count: {vendorDealCount}
                      </Typography.Text>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty className="my-5" image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Vantage vendors" />
        )}
      </div>

      <Divider />

      {vendor ? (
        <VendorForm
          vendor={vendor}
          selectedVendor={selectedVendor}
          loading={updating}
          form={form}
          addOrEditVendor={addOrEditVendor}
          onFinish={handleSubmit}
          onCancel={handleCancel}
          headerActions={
            <Space size={6}>
              <Tooltip title={addOrEditVendor ? 'Cancel Vantage edit' : 'Edit Vantage vendor'}>
                <Button
                  aria-label={addOrEditVendor ? 'Cancel Vantage edit' : 'Edit Vantage vendor'}
                  onClick={() => (addOrEditVendor ? handleCancel() : handleEdit())}
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  disabled={!vendor}
                />
              </Tooltip>
              {canDeleteVendor && (
                <Tooltip title="Delete Vantage vendor">
                  <Button
                    aria-label="Delete Vantage vendor"
                    danger
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={addOrEditVendor}
                    loading={isActionLoading}
                    onClick={openDeleteModal}
                  />
                </Tooltip>
              )}
            </Space>
          }
        />
      ) : (
        <Empty
          className="my-10"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a Vantage vendor to review details"
        />
      )}
    </Card>
  </Col>
);
